<template>
  <article
    :data-testid="`participant-row-${participant.id}`"
    :data-confetti-highlighted="isConfettiHighlighted ? 'true' : 'false'"
    :data-participant-role="participant.role"
    :data-highlighted="isHighlighted ? 'true' : 'false'"
    :data-is-drawing="isDrawing ? 'true' : 'false'"
    :data-is-typing="participant.isTyping ? 'true' : 'false'"
    :data-selected="isSelected ? 'true' : 'false'"
    :data-hand-raised="participant.hand ? 'true' : 'false'"
    :data-has-active-signals="hasActiveSignals ? 'true' : 'false'"
    :data-emoji-value="participantEmojiValue"
    :data-status-value="participantStatusValue"
    :data-feedback-value="participantFeedbackValue"
    :data-track-audio="participant.trackState.audio ? 'on' : 'off'"
    :data-track-camera="participant.trackState.camera ? 'on' : 'off'"
    :data-track-screenshare="participant.trackState.screenshare ? 'on' : 'off'"
    :data-viewer-cloaked="participant.isViewer && viewerCloaked ? 'true' : 'false'"
    @click="emit('select', $event)"
    @contextmenu.prevent.stop="emit('contextmenu', $event)"
    @dragstart="emit('dragstart', $event)"
    @dragend="emit('dragend')"
    :draggable="draggable ? true : undefined"
    :class="[
      compact
        ? compactRowClass
        : 'rounded-2xl border border-slate-700/70 bg-slate-900/70 px-3 py-3 shadow-sm shadow-slate-950/30',
      rowHighlightClass,
    ]"
  >
    <template v-if="compact">
      <div class="text-conference-ink-soft flex min-w-0 items-center text-[11px] leading-none">
        <button
          v-if="showParticipationCount && participant.role !== 'teacher'"
          type="button"
          :data-testid="`participant-participation-${participant.id}`"
          class="border-conference-accent-border bg-conference-accent-surface text-conference-accent-muted hover:border-conference-accent-border hover:bg-conference-accent-surface shrink-0 rounded-full border px-1 py-px text-[10px] font-semibold transition"
          :title="`Participation count: ${participant.participationCount}`"
          @click.exact.stop="emit('adjustParticipation', 1)"
          @click.shift.exact.stop="emit('adjustParticipation', -1)"
        >
          {{ participant.participationCount }}
        </button>
      </div>
      <p class="flex min-w-0 items-center gap-1 text-[11px] leading-none font-semibold" :class="nameClass">
        <span v-if="participant.hand" class="shrink-0 text-[10px] font-semibold">
          {{ `${handIndexLabel}✋` }}
        </span>
        <span v-if="participant.emoji">{{ participant.emoji }}</span>
        <span v-if="participant.status">
          {{ participant.status }}
        </span>
        <span
          :data-testid="`participant-name-${participant.id}`"
          :data-confetti-highlighted="isConfettiHighlighted ? 'true' : 'false'"
          :class="['min-w-0 truncate', displayNameClass]"
          :title="participant.isViewer && viewerCloaked ? 'Cloaking active' : undefined"
        >
          {{ participant.displayName }}
        </span>
      </p>
      <span class="flex shrink-0 items-center gap-1 justify-self-end text-zinc-500">
        <span v-if="showAudioIcon" class="relative flex size-3.5 shrink-0 items-center justify-center">
          <span v-if="showSpeakingPulse" aria-hidden="true" class="participant-activity-ring" />
          <IconLucideMic class="size-3.5" :class="audioIconClass" />
        </span>
        <IconLucideVideo v-if="showVideoIcon" class="size-3.5" :class="rosterIconClass(participant.trackState.camera)" />
        <span v-if="showChatIcon" class="relative flex size-3.5 shrink-0 items-center justify-center">
          <span v-if="showTypingPulse" aria-hidden="true" class="participant-activity-ring" />
          <IconLucideMessageSquareMore class="size-3.5" :class="chatIconClass" />
        </span>
        <span v-if="showWhiteboardIcon" class="relative flex size-3.5 shrink-0 items-center justify-center">
          <span v-if="showDrawingPulse" aria-hidden="true" class="participant-activity-ring" />
          <IconLucidePencilLine class="size-3.5" :class="whiteboardIconClass" />
        </span>
        <IconLucidePresentation
          v-if="showScreenshareIcon"
          class="size-3.5"
          :class="rosterIconClass(participant.trackState.screenshare)"
        />
        <IconLucideCrown
          v-if="participant.permissions.topMenuControl"
          class="size-3.5"
          :class="rosterIconClass(participant.permissions.topMenuControl)"
        />
      </span>
      <span v-if="visibleFeedback" class="text-conference-accent-text shrink-0 justify-self-end text-[10px] font-semibold">
        <span v-if="participantFeedbackValue === 'faster'" class="inline-flex items-center">
          <IconLucideArrowRight aria-hidden="true" class="size-3" />
        </span>
        <span v-else-if="participantFeedbackValue === 'slower'" class="inline-flex items-center">
          <IconLucideArrowLeft aria-hidden="true" class="size-3" />
        </span>
        <span v-else>{{ visibleFeedback }}</span>
      </span>
    </template>

    <template v-else>
      <div class="flex items-start gap-3">
        <div
          :class="[
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tracking-[0.2em] uppercase',
            participant.role === 'teacher'
              ? 'border-amber-400/60 bg-amber-500/10 text-amber-100'
              : 'border-sky-400/40 bg-sky-500/10 text-sky-100',
          ]"
          :aria-hidden="true"
        >
          {{ initials }}
        </div>

        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-center gap-2">
            <p class="truncate text-sm font-semibold text-slate-50">
              <span :class="displayNameClass">{{ participant.displayName }}</span>
            </p>
            <span
              v-if="participant.isViewer"
              class="rounded-full border border-sky-400/50 bg-sky-500/10 px-2 py-0.5 text-[10px] font-semibold tracking-[0.2em] text-sky-100 uppercase"
            >
              You
            </span>
            <span
              :class="[
                'rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-[0.2em] uppercase',
                participant.role === 'teacher' ? 'border-amber-400/50 text-amber-100' : 'border-slate-600 text-slate-300',
              ]"
            >
              {{ participant.role }}
            </span>
          </div>

          <div class="mt-1 flex flex-wrap gap-2 text-xs text-slate-300">
            <span
              v-if="participant.hand"
              class="rounded-full border border-emerald-400/50 bg-emerald-500/10 px-2 py-0.5 text-emerald-100"
            >
              Hand raised
            </span>
            <span
              v-if="participant.emoji"
              class="rounded-full border border-fuchsia-400/50 bg-fuchsia-500/10 px-2 py-0.5 text-fuchsia-100"
            >
              {{ participant.emoji }}
            </span>
            <span
              v-if="participant.status"
              class="rounded-full border border-indigo-400/50 bg-indigo-500/10 px-2 py-0.5 text-indigo-100"
            >
              {{ participant.status }}
            </span>
            <span
              v-if="visibleFeedback"
              class="rounded-full border border-emerald-400/50 bg-emerald-500/10 px-2 py-0.5 text-emerald-100"
            >
              <span v-if="participantFeedbackValue === 'faster'" class="inline-flex items-center">
                <IconLucideArrowRight aria-hidden="true" class="size-3.5" />
              </span>
              <span v-else-if="participantFeedbackValue === 'slower'" class="inline-flex items-center">
                <IconLucideArrowLeft aria-hidden="true" class="size-3.5" />
              </span>
              <span v-else>{{ visibleFeedback }}</span>
            </span>
            <span
              v-if="!participant.hand && !participant.emoji && !participant.status && !visibleFeedback"
              class="rounded-full border border-slate-700 px-2 py-0.5 text-slate-400"
            >
              No active signals
            </span>
          </div>
        </div>
      </div>

      <div :data-testid="`participant-track-badges-${participant.id}`" class="mt-3 flex flex-wrap gap-2 text-xs text-slate-300">
        <span :class="trackBadgeClass(participant.trackState.audio)">
          {{ participant.trackState.audio ? 'Mic on' : 'Mic muted' }}
        </span>
        <span :class="trackBadgeClass(participant.trackState.camera)">
          {{ participant.trackState.camera ? 'Camera on' : 'Camera off' }}
        </span>
        <span :class="trackBadgeClass(participant.trackState.screenshare)">
          {{ participant.trackState.screenshare ? 'Sharing screen' : 'Not sharing' }}
        </span>
      </div>
    </template>
  </article>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import IconLucideArrowLeft from '~icons/lucide/arrow-left';
import IconLucideArrowRight from '~icons/lucide/arrow-right';
import IconLucideCrown from '~icons/lucide/crown';
import IconLucideMic from '~icons/lucide/mic';
import IconLucideMessageSquareMore from '~icons/lucide/message-square-more';
import IconLucidePencilLine from '~icons/lucide/pencil-line';
import IconLucidePresentation from '~icons/lucide/presentation';
import IconLucideVideo from '~icons/lucide/video';
import type { ConferenceRosterParticipant } from '../convex/useConference';
import { formatFeedbackDisplay, normalizeFeedbackValue } from '../features/feedback';

const props = defineProps<{
  chatVisible?: boolean;
  compact?: boolean;
  draggable?: boolean;
  feedbackVisibility?: boolean;
  isConfettiHighlighted?: boolean;
  handIndex?: number;
  isDragged?: boolean;
  isDrawing?: boolean;
  isHighlighted?: boolean;
  isSpeaking?: boolean;
  isSelected?: boolean;
  participant: ConferenceRosterParticipant;
  showParticipationCount?: boolean;
  viewerCloaked?: boolean;
}>();

const emit = defineEmits<{
  (event: 'adjustParticipation', delta: number): void;
  (event: 'contextmenu', mouseEvent: MouseEvent): void;
  (event: 'dragend'): void;
  (event: 'dragstart', dragEvent: DragEvent): void;
  (event: 'select', mouseEvent: MouseEvent): void;
}>();

const initials = computed(
  () =>
    props.participant.displayName
      .split(/\s+/u)
      .filter(Boolean)
      .slice(0, 2)
      .map((segment) => segment.charAt(0))
      .join('') || '?',
);

const compactRowClass = computed(() => {
  const classes = [
    'col-span-full',
    'grid',
    'grid-cols-subgrid',
    'items-center',
    'gap-x-1',
    'px-0.5',
    'py-px',
    'transition-[background-color,outline-color,opacity,transform]',
    'duration-150',
  ];

  if (props.draggable) {
    classes.push('cursor-grab', 'select-none');
  }

  if (props.participant.hand) {
    classes.push('bg-conference-room');
  }

  if (props.participant.trackState.audio && props.isSpeaking) {
    classes.push('bg-conference-accent-surface');
  }

  if (props.isSelected) {
    classes.push(
      'outline',
      'outline-1',
      'outline-conference-accent-muted',
      'bg-conference-accent-surface',
      'shadow-[inset_0_0_0_1px_rgba(125,105,26,0.15)]',
    );
  }

  if (props.isDragged) {
    classes.push(
      'bg-conference-accent-surface',
      'opacity-65',
      'scale-[0.985]',
      'outline',
      'outline-1',
      'outline-conference-accent-muted',
    );
  }

  return classes.join(' ');
});

const handIndexLabel = computed(() => {
  return props.handIndex ? `${props.handIndex}` : '';
});

const nameClass = computed(() => {
  const classes = [props.participant.isViewer ? 'font-bold text-conference-ink-strong' : 'text-conference-ink'];

  if (props.participant.isViewer && props.viewerCloaked) {
    classes.push('text-[#3b0764]', 'italic', 'underline', 'decoration-dotted', 'decoration-2', 'underline-offset-2');
  }

  if (props.participant.status === '⏳') {
    classes.push('opacity-40');
  }

  return classes.join(' ');
});

const displayNameClass = computed(() => {
  return props.isConfettiHighlighted ? 'participant-confetti-name' : 'inline-block max-w-full';
});

const rowHighlightClass = computed(() => {
  if (!props.isHighlighted) {
    return '';
  }

  return 'outline outline-1 outline-amber-400/80 bg-amber-400/10 shadow-[inset_0_0_0_1px_rgba(251,191,36,0.16)]';
});

const visibleFeedback = computed(() => {
  const normalizedFeedbackValue = normalizeFeedbackValue(props.participant.feedback);

  if (!normalizedFeedbackValue) {
    return '';
  }

  if (!(props.feedbackVisibility || props.participant.isViewer)) {
    return '';
  }

  return formatFeedbackDisplay(normalizedFeedbackValue);
});
const participantEmojiValue = computed(() => props.participant.emoji || 'none');
const participantStatusValue = computed(() => props.participant.status || 'none');
const participantFeedbackValue = computed(() => normalizeFeedbackValue(props.participant.feedback) || 'none');
const hasActiveSignals = computed(
  () =>
    props.participant.hand ||
    Boolean(props.participant.emoji) ||
    Boolean(props.participant.status) ||
    Boolean(visibleFeedback.value),
);

const showAudioIcon = computed(() => props.participant.role === 'teacher' || props.participant.permissions.audio);
const showVideoIcon = computed(() => props.participant.role === 'teacher' || props.participant.permissions.video);
const showChatIcon = computed(
  () => props.chatVisible && (props.participant.role === 'teacher' || props.participant.permissions.chat),
);
const showWhiteboardIcon = computed(() => props.participant.role === 'teacher' || props.participant.permissions.whiteboard);
const showScreenshareIcon = computed(() => props.participant.role === 'teacher' || props.participant.permissions.screenshare);
const showSpeakingPulse = computed(() => props.participant.trackState.audio && props.isSpeaking);
const showTypingPulse = computed(() => props.participant.isTyping);
const showDrawingPulse = computed(() => Boolean(props.isDrawing));
const audioIconClass = computed(() => {
  const classes = [rosterIconClass(props.participant.trackState.audio)];

  if (showSpeakingPulse.value) {
    classes.push('participant-activity-icon', 'text-black', 'stroke-[3]');
  }

  return classes.join(' ');
});
const chatIconClass = computed(() => buildPulsingIconClass(props.participant.isTyping));
const whiteboardIconClass = computed(() => buildPulsingIconClass(Boolean(props.isDrawing)));

function trackBadgeClass(isActive: boolean, compact = false): string {
  const base = compact ? 'rounded-full border px-1.5 py-0.5' : 'rounded-full border px-2 py-0.5';

  return isActive
    ? `${base} border-emerald-400/50 bg-emerald-500/10 text-emerald-100`
    : `${base} border-slate-700 text-slate-400`;
}

function rosterIconClass(isActive: boolean): string {
  return isActive ? 'text-black opacity-100 stroke-[3]' : 'text-zinc-500 opacity-80';
}

function buildPulsingIconClass(isActive: boolean): string {
  const classes = [rosterIconClass(isActive)];

  if (isActive) {
    classes.push('participant-activity-icon', 'text-black', 'stroke-[3]');
  }

  return classes.join(' ');
}
</script>

<style scoped>
.participant-activity-ring {
  inset: -3px;
  position: absolute;
  border-radius: 9999px;
  pointer-events: none;
  animation: participant-activity-ring 1.2s ease-in-out infinite;
}

.participant-activity-icon {
  transform-origin: center;
  animation: participant-activity-icon 1.2s ease-in-out infinite;
  filter: drop-shadow(0 0 4px rgba(109, 92, 25, 0.4));
}

.participant-confetti-name {
  animation: participant-confetti-name 1s ease-in-out infinite;
  border-radius: 3px;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  padding-left: 5px;
  padding-right: 5px;
  position: relative;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes participant-activity-ring {
  0% {
    box-shadow: 0 0 0 0 rgba(109, 92, 25, 0.38);
    opacity: 0.95;
  }

  65% {
    box-shadow: 0 0 0 5px rgba(109, 92, 25, 0);
    opacity: 0.2;
  }

  100% {
    box-shadow: 0 0 0 0 rgba(109, 92, 25, 0);
    opacity: 0;
  }
}

@keyframes participant-activity-icon {
  0%,
  100% {
    transform: scale(1);
  }

  35% {
    transform: scale(1.18);
  }

  65% {
    transform: scale(1.08);
  }
}

@keyframes participant-confetti-name {
  0% {
    left: -10px;
    background-color: yellow;
    text-shadow: 0 0 10px #fff;
  }

  25% {
    left: 0;
    text-shadow: 0 0 15px rgb(202, 182, 66);
  }

  50% {
    left: 10px;
    text-shadow: 0 0 20px yellow;
  }

  75% {
    left: 0;
    text-shadow: 0 0 15px rgb(202, 182, 66);
  }

  100% {
    left: -10px;
    text-shadow: 0 0 10px #fff;
  }
}
</style>
